import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  UserCheck, 
  Video, 
  FileText, 
  UserPlus,
  Calendar,
  TrendingUp,
  RefreshCw,
  Clock,
  Activity,
  MessageCircle,
  Star,
  Megaphone,
  Sparkles
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('7');
  const [refreshing, setRefreshing] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartGranularity, setChartGranularity] = useState('daily');

  const periods = [
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: '365', label: 'Last Year' }
  ];

  useEffect(() => {
    // Check if user is admin
    if (user && user.email && user.email.toLowerCase() !== 'info@showstop.ai') {
      toast.error('Access denied. Admin only.');
      navigate('/trend-spotter');
      return;
    }

    fetchStats();
    fetchActivityLogs();
    fetchFeedback();
    fetchChartData();
  }, [selectedPeriod, chartGranularity, user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/dashboard?period=${selectedPeriod}`, {
        withCredentials: true
      });

      if (response.data.success) {
        setStats(response.data.stats);
      } else {
        toast.error(response.data.error || 'Failed to fetch statistics');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin only.');
        navigate('/trend-spotter');
      } else {
        toast.error('Failed to fetch dashboard statistics');
        console.error('Error fetching stats:', error);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setLoadingLogs(true);
      const response = await axios.get(`/api/admin/activity-logs?period=${selectedPeriod}&limit=100`, {
        withCredentials: true
      });

      if (response.data.success) {
        setActivityLogs(response.data.logs || []);
      } else {
        console.error('Failed to fetch activity logs:', response.data.error);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchFeedback = async () => {
    try {
      setLoadingFeedback(true);
      const response = await axios.get('/api/admin/feedback?limit=100', {
        withCredentials: true
      });

      if (response.data.success) {
        setFeedback(response.data.feedback || []);
        setFeedbackTotal(response.data.total || 0);
      } else {
        console.error('Failed to fetch feedback:', response.data.error);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        // Admin only, ignore silently
      } else {
        console.error('Error fetching feedback:', error);
      }
    } finally {
      setLoadingFeedback(false);
    }
  };

  const fetchChartData = async () => {
    try {
      setLoadingChart(true);
      const response = await axios.get(`/api/admin/active-users-chart?period=${selectedPeriod}&granularity=${chartGranularity}`, {
        withCredentials: true
      });

      if (response.data.success) {
        setChartData(response.data.data || []);
      } else {
        console.error('Failed to fetch chart data:', response.data.error);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
    fetchActivityLogs();
    fetchFeedback();
    fetchChartData();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_created':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'user_login':
        return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'video_added':
        return <Video className="h-4 w-4 text-purple-500" />;
      case 'brief_created':
        return <FileText className="h-4 w-4 text-orange-500" />;
      case 'collaborator_added':
        return <UserPlus className="h-4 w-4 text-pink-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActivityBadgeColor = (type) => {
    switch (type) {
      case 'user_created':
        return 'bg-blue-100 text-blue-800';
      case 'user_login':
        return 'bg-green-100 text-green-800';
      case 'video_added':
        return 'bg-purple-100 text-purple-800';
      case 'brief_created':
        return 'bg-orange-100 text-orange-800';
      case 'collaborator_added':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const StatCard = ({ icon: Icon, title, value, color = 'blue', tooltip = null }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
      pink: 'bg-pink-50 text-pink-600 border-pink-200'
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow relative group">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-sm font-medium text-gray-600">{title}</p>
              {tooltip && (
                <div className="relative">
                  <span className="text-xs text-gray-400 cursor-help">ⓘ</span>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap shadow-lg">
                      {tooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-gray-900">{value?.toLocaleString() || '0'}</p>
          </div>
          <div className={`p-3 rounded-lg border-2 ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Internal statistics and metrics</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {periods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => navigate('/weekly-digest')}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileText size={18} />
                <span>Weekly Digest</span>
              </button>
              <button
                onClick={() => navigate('/admin/crm')}
                className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Users size={18} />
                <span>CRM</span>
              </button>
              <button
                onClick={() => navigate('/admin/brief-digest-reviews')}
                className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Sparkles size={18} />
                <span>Friday briefs</span>
              </button>
              <button
                onClick={() => navigate('/admin/digest-ads')}
                className="flex items-center space-x-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
              >
                <Megaphone size={18} />
                <span>Digest ads</span>
              </button>
              <button
                onClick={() => navigate('/admin/trend-video-social')}
                className="flex items-center space-x-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
              >
                <span>🎬</span>
                <span>Trend Videos</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
          {stats && (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Calendar size={16} />
              <span>{stats.periodLabel}</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <StatCard
              icon={Users}
              title="New Users"
              value={stats.newUsers}
              color="blue"
              tooltip="Users created during this period"
            />
            <StatCard
              icon={UserCheck}
              title="Active Users"
              value={stats.activeUsers}
              color="green"
              tooltip="All users (new + existing) with activity during this period"
            />
            <StatCard
              icon={Video}
              title="Videos Added"
              value={stats.videosAdded}
              color="purple"
              tooltip="Videos added to library during this period"
            />
            <StatCard
              icon={FileText}
              title="Briefs Created"
              value={stats.briefsCreated}
              color="orange"
              tooltip="Content briefs created during this period"
            />
            <StatCard
              icon={UserPlus}
              title="Collaborators Added"
              value={stats.collaboratorsAdded}
              color="pink"
              tooltip="Collaborators added to briefs during this period"
            />
          </div>
        )}

        {/* Active Users Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <TrendingUp size={20} className="mr-2 text-indigo-600" />
              Active Users Trend
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setChartGranularity('daily')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  chartGranularity === 'daily'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => setChartGranularity('weekly')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  chartGranularity === 'weekly'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Weekly
              </button>
            </div>
          </div>
          {loadingChart ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <p>No data available for this period</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="label" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Active Users', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value) => [value, 'Active Users']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="activeUsers" 
                  stroke="#4f46e5" 
                  strokeWidth={2}
                  dot={{ fill: '#4f46e5', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Active Users"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Summary Section */}
        {stats && (
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp size={20} className="mr-2 text-indigo-600" />
              Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">User Growth</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.newUsers} new {stats.newUsers === 1 ? 'user' : 'users'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.activeUsers} total active {stats.activeUsers === 1 ? 'user' : 'users'} ({stats.activeUsers - stats.newUsers > 0 ? `+${stats.activeUsers - stats.newUsers} existing` : 'all new'})
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Content Activity</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.videosAdded} {stats.videosAdded === 1 ? 'video' : 'videos'} added
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.briefsCreated} {stats.briefsCreated === 1 ? 'brief' : 'briefs'} created
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Collaboration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.collaboratorsAdded} {stats.collaboratorsAdded === 1 ? 'collaborator' : 'collaborators'} added
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Engagement Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.newUsers > 0 
                    ? ((stats.activeUsers / stats.newUsers) * 100).toFixed(1)
                    : '0'}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Active users / New users
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Activity Logs Table */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Activity size={20} className="mr-2 text-indigo-600" />
              Activity Logs
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Detailed user activity and behavior logs for the selected period
            </p>
          </div>
          
          {loadingLogs ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading activity logs...</p>
            </div>
          ) : activityLogs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock size={32} className="mx-auto mb-2 text-gray-400" />
              <p>No activity logs found for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activity Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activityLogs.map((log, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Clock size={14} className="mr-2 text-gray-400" />
                          {formatTimestamp(log.activity_time)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                            <Users size={16} className="text-indigo-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.user_name || log.user_email?.split('@')[0] || 'Unknown'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {log.user_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActivityBadgeColor(log.activity_type)}`}>
                          {getActivityIcon(log.activity_type)}
                          <span className="ml-1.5 capitalize">
                            {log.activity_type?.replace('_', ' ')}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.activity_description}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {log.related_data ? (
                          <div className="space-y-1">
                            {log.related_data.platform && (
                              <div>Platform: <span className="font-medium">{log.related_data.platform}</span></div>
                            )}
                            {log.related_data.status && (
                              <div className="font-semibold">Status: <span className="font-medium text-indigo-600">{log.related_data.status}</span></div>
                            )}
                            {log.related_data.permission && (
                              <div>Permission: <span className="font-medium">{log.related_data.permission}</span></div>
                            )}
                            {log.related_data.angle && (
                              <div className="max-w-md" title={log.related_data.angle}>
                                <div className="font-semibold text-gray-700">Angle:</div>
                                <div className="font-medium text-gray-900 mt-0.5">{log.related_data.angle}</div>
                              </div>
                            )}
                            {log.related_data.shots && Array.isArray(log.related_data.shots) && log.related_data.shots.length > 0 && (
                              <div className="max-w-md mt-2">
                                <div className="font-semibold text-gray-700">Shots:</div>
                                <div className="text-gray-900 mt-0.5">{log.related_data.shots.join(', ')}</div>
                              </div>
                            )}
                            {log.related_data.caption && (
                              <div className="max-w-md mt-2">
                                <div className="font-semibold text-gray-700">Caption:</div>
                                <div className="text-gray-900 mt-0.5">{log.related_data.caption}</div>
                              </div>
                            )}
                            {log.related_data.notes && (
                              <div className="max-w-md mt-2">
                                <div className="font-semibold text-gray-700">Notes:</div>
                                <div className="text-gray-900 mt-0.5">{log.related_data.notes}</div>
                              </div>
                            )}
                            {log.related_data.source_url && (
                              <div className="truncate max-w-xs" title={log.related_data.source_url}>
                                <a 
                                  href={log.related_data.source_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:text-indigo-800 underline"
                                >
                                  View Source
                                </a>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {activityLogs.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                Showing {activityLogs.length} activity log{activityLogs.length === 1 ? '' : 's'} for {stats?.periodLabel || 'selected period'}
              </p>
            </div>
          )}
        </div>

        {/* User Feedback Section */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <MessageCircle size={20} className="mr-2 text-indigo-600" />
              User Feedback
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              All user feedback and ratings ({feedbackTotal} total)
            </p>
          </div>
          
          {loadingFeedback ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading feedback...</p>
            </div>
          ) : feedback.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle size={32} className="mx-auto mb-2 text-gray-400" />
              <p>No feedback submitted yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Favorite Features
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Top Asks / Suggestions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {feedback.map((item, index) => (
                    <tr key={item.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Clock size={14} className="mr-2 text-gray-400" />
                          {formatTimestamp(item.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                            <Users size={16} className="text-indigo-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.user_email?.split('@')[0] || 'Unknown'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.user_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <Star
                              key={rating}
                              size={16}
                              className={
                                rating <= item.overall_rating
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }
                            />
                          ))}
                          <span className="ml-2 text-sm font-medium text-gray-900">
                            {item.overall_rating}/5
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {item.favorite_features && item.favorite_features.length > 0 ? (
                            item.favorite_features.map((feature, idx) => {
                              const featureLabels = {
                                video_library: { label: '📋 Video Library', color: 'bg-purple-100 text-purple-800' },
                                trend_library: { label: '📈 Trend Library', color: 'bg-blue-100 text-blue-800' },
                                content_brief: { label: '📝 Content Brief', color: 'bg-orange-100 text-orange-800' }
                              };
                              const featureInfo = featureLabels[feature] || { label: feature, color: 'bg-gray-100 text-gray-800' };
                              return (
                                <span
                                  key={idx}
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${featureInfo.color}`}
                                >
                                  {featureInfo.label}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md">
                          {item.top_asks ? (
                            <p className="whitespace-pre-wrap">{item.top_asks}</p>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {feedback.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                Showing {feedback.length} of {feedbackTotal} feedback entries
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
